import { ApolloProvider } from "@apollo/client/react";
import { QueryClientProvider } from "@tanstack/react-query";
import { App as F7App } from "framework7-react";
import { FC, PropsWithChildren, useEffect, useMemo } from "react";
import { I18nextProvider } from "react-i18next";

import { apolloClient } from "@/config/apolloClient";
import { i18n } from "@/config/i18n";
import { queryClient } from "@/config/queryClient";
import routes from "@/config/routes";
import { useInitializeCapacitor } from "@/hooks/useInitializeCapacitor";
import { useIsEnabledCapacitor } from "@/hooks/useIsEnabledCapacitor";
import { F7AppProps } from "@/types/framework7-react";

export type AppProviderProps = PropsWithChildren<{
  theme?: "md" | "ios";
}>;

export const AppProvider: FC<AppProviderProps> = ({ children, theme = "md" }) => {
  useInitializeCapacitor();

  const isEnabledCapacitor = useIsEnabledCapacitor();

  const input = useMemo<F7AppProps["input"]>(() => {
    return { scrollIntoViewCentered: isEnabledCapacitor, scrollIntoViewOnFocus: isEnabledCapacitor };
  }, [isEnabledCapacitor]);

  const serviceWorker = useMemo<F7AppProps["serviceWorker"]>(() => {
    if (process.env.NODE_ENV === "production") {
      return {
        path: "/service-worker.js",
      };
    }
    return {};
  }, []);

  useEffect(() => {
    return () => {
      console.log("Framework7 change theme:", theme);
      window.location.reload();
    };
  }, [theme]);

  return (
    <QueryClientProvider client={queryClient}>
      <ApolloProvider client={apolloClient}>
        <I18nextProvider i18n={i18n}>
          <F7App
            colors={{
              bad: "#51A2FF",
              education: "#99E600",
              family: "#FFE020",
              good: "#FB64B6",
              life: "#FF8903",
              other: "#f87171",
              primary: "#101010",
              private: "#F3A8FF",
              sleep: "#A683FF",
              unset: "#D1D5DB",
              work: "#A683FF",
            }}
            input={input}
            name="Timefill"
            routes={routes}
            serviceWorker={serviceWorker}
            statusbar={{
              androidOverlaysWebView: true,
              iosOverlaysWebView: true,
            }}
            theme={theme}
          >
            {children}
          </F7App>
        </I18nextProvider>
      </ApolloProvider>
    </QueryClientProvider>
  );
};
